// The installer's entry point (NAV-010 `/installer`): the Installs tab lists every installer
// session, filterable by customer and status (a row opens its session's console), with the
// first-run empty state and the form that starts a new one (FLOW-008). `/installer?view=accounts` is the Linked AWS accounts tab before a
// customer is chosen (the accounts screen itself is per customer).
import { useContext, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import type { ScreenProps } from "../../router/route";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { RouterContext } from "../../router/router";
import { Link } from "../../ui/shell";
import { DataTable, type Column } from "@nexus/ui";
import { Dialog } from "@nexus/ui";
import { Field, errorId, hintId } from "@nexus/ui";
import { CustomerPicker, useCustomerName } from "../../ui/customer-directory";
import { Chip, EmptyState, FilterBar, NameCell } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { articleHref } from "../../help/articles";
import { InstallerDisabled } from "./disabled";
import { InstallerHeader } from "./header";
import {
  ACCESS_MODES,
  GOALS,
  SESSION_STATES,
  accessModeHint,
  accessModeLabel,
  accountRow,
  createSessionErrorMessage,
  createSessionRequest,
  emptyCreateSessionDraft,
  emptySessionListFilter,
  goalLabel,
  installerAccountsPath,
  installerIndexTab,
  installerSessionPath,
  isFirstRun,
  isInstallerDisabled,
  linkedAccountChoices,
  sessionListQuery,
  sessionRow,
  sessionStateLabel,
  validateCreateSession,
  type CreateSessionDraft,
  type InstallerAccessMode,
  type InstallerGoal,
  type SessionListFilter,
  type SessionRow,
} from "./model";

interface ListState {
  loading: boolean;
  error: ApiError | null;
  rows: SessionRow[];
  nextCursor: string | null;
}

const emptyList: ListState = { loading: true, error: null, rows: [], nextCursor: null };

/** "Sep 24, 2026, 14:05" in the reader's locale. */
function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function CustomerCell({ orgId, href }: { orgId: string; href: string }) {
  const name = useCustomerName(orgId);
  return <NameCell name={name} id={orgId} href={href} />;
}

export function SessionList({ query }: ScreenProps<"/installer">) {
  return installerIndexTab(query) === "accounts" ? <AccountsChooser /> : <Installs />;
}

function Installs() {
  const api = useApi();
  const router = useContext(RouterContext);
  const [filter, setFilter] = useState<SessionListFilter>(emptySessionListFilter);
  const [list, setList] = useState<ListState>(emptyList);
  const [creating, setCreating] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setList((current) => ({ ...current, loading: true, error: null }));
    api
      .get("/v1/installer/sessions", { query: sessionListQuery(filter) })
      .then((page) => {
        if (cancelled) return;
        setList({ loading: false, error: null, rows: page.items.map(sessionRow), nextCursor: page.nextCursor });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setList({ loading: false, error, rows: [], nextCursor: null });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [api, filter, reloadToken]);

  const columns: readonly Column<SessionRow>[] = [
    {
      key: "orgId",
      header: "Customer",
      cell: (row) => <CustomerCell orgId={row.orgId} href={row.href} />,
      compare: (a, b) => a.orgId.localeCompare(b.orgId),
    },
    { key: "awsAccountId", header: "AWS account", cell: (row) => <span class="mono">{row.awsAccountId}</span> },
    { key: "goalLabel", header: "Goal", cell: (row) => row.goalLabel, help: "installer.goal" },
    { key: "accessModeLabel", header: "Access", cell: (row) => row.accessModeLabel, help: "installer.accessMode" },
    {
      key: "stateLabel",
      header: "Status",
      help: "installer.status",
      cell: (row) => <Chip tone={row.stateTone}>{row.stateLabel}</Chip>,
      compare: (a, b) => a.stateLabel.localeCompare(b.stateLabel),
    },
    { key: "pendingChanges", header: "Pending changes", cell: (row) => row.pendingChanges, numeric: true, help: "installer.pendingChanges" },
    { key: "createdAt", header: "Started", cell: (row) => when(row.createdAt), compare: (a, b) => a.createdAt.localeCompare(b.createdAt) },
  ];

  if (!api) return <p role="alert">Admin API not available.</p>;

  if (isInstallerDisabled(list.error)) return <InstallerDisabled />;

  const start = (
    <button type="button" class="button button-primary" onClick={() => setCreating(true)}>
      <Icon name="plus" />
      Start an install
    </button>
  );
  const firstRun = isFirstRun(filter, list);

  return (
    <div class="page installer-sessions">
      <InstallerHeader tab="installs" orgId={filter.orgId.trim() || null} actions={firstRun ? undefined : start} />
      {firstRun ? (
        <EmptyState
          title="No installs yet"
          lede="An install takes about 20 minutes. You need the customer's AWS account ID and someone at the customer who can run one CloudFormation stack."
          steps={[
            {
              title: "Link the AWS account",
              text: "The customer opens a link we give you and creates a small stack. It lets the installer act only inside a narrow, audited role.",
            },
            {
              title: "Review the plan",
              text: "The agent inspects the account and proposes each change: network, proxy, sign-in. Nothing is created until you approve.",
            },
            {
              title: "Verify and hand over",
              text: "The installer checks the proxy answers, then shows the Claude Code and Codex setup for the customer's developers.",
            },
          ]}
          actions={
            <>
              {start}
              <Link href={articleHref("installer")}>Read the installer guide</Link>
            </>
          }
        />
      ) : (
        <>
          <FilterBar>
            <Field id="sessions-customer" label="Customer" help="common.customer" compact>
              <CustomerPicker id="sessions-customer" value={filter.orgId} allowAll onChange={(orgId) => setFilter({ ...filter, orgId })} />
            </Field>
            <Field id="sessions-status" label="Status" help="installer.status" compact>
              <select
                id="sessions-status"
                value={filter.state}
                onChange={(event) => setFilter({ ...filter, state: event.currentTarget.value as SessionListFilter["state"] })}
              >
                <option value="">Any status</option>
                {SESSION_STATES.map((state) => (
                  <option value={state} key={state}>
                    {sessionStateLabel(state)}
                  </option>
                ))}
              </select>
            </Field>
          </FilterBar>
          <div class="table-card">
            <DataTable
              caption="Installer sessions"
              hideCaption
              columns={columns}
              rows={list.rows}
              rowKey={(row) => row.id}
              loading={list.loading}
              error={list.error}
              onRetry={() => setReloadToken((n) => n + 1)}
              emptyMessage="No installer sessions match these filters."
              nextCursor={list.nextCursor}
              onRowClick={(row) => router?.navigate(row.href)}
              rowLabel={(row) => `Open the session for ${row.awsAccountId}`}
            />
          </div>
        </>
      )}
      <NewSessionDialog
        open={creating}
        initialOrgId={filter.orgId.trim()}
        onClose={() => setCreating(false)}
        onCreated={(sessionId) => {
          setCreating(false);
          if (router) router.navigate(installerSessionPath(sessionId));
        }}
      />
    </div>
  );
}

/** The Linked AWS accounts tab before a customer is chosen: pick one, go to its accounts. */
function AccountsChooser() {
  const router = useContext(RouterContext);
  return (
    <div class="page installer-accounts-chooser">
      <InstallerHeader tab="accounts" orgId={null} />
      <section class="card" aria-labelledby="accounts-chooser-heading">
        <h2 id="accounts-chooser-heading">Choose a customer</h2>
        <p class="muted">
          AWS accounts are linked per customer. Choose one to see its linked accounts, link a new one, verify or unlink.
        </p>
        <Field id="accounts-customer" label="Customer" help="common.customer">
          <CustomerPicker
            id="accounts-customer"
            value=""
            onChange={(orgId) => {
              if (orgId && router) router.navigate(installerAccountsPath(orgId));
            }}
          />
        </Field>
      </section>
    </div>
  );
}

function NewSessionDialog({
  open,
  initialOrgId,
  onClose,
  onCreated,
}: {
  open: boolean;
  initialOrgId: string;
  onClose(): void;
  onCreated(sessionId: string): void;
}) {
  const api = useApi();
  const [draft, setDraft] = useState<CreateSessionDraft>(emptyCreateSessionDraft);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [linked, setLinked] = useState<{ awsAccountId: string; verified: boolean }[]>([]);
  const errors = validateCreateSession(draft);
  const orgId = draft.orgId.trim();

  useEffect(() => {
    if (open) {
      setDraft({ ...emptyCreateSessionDraft, orgId: initialOrgId });
      setTouched(false);
      setError(null);
    }
  }, [open]);

  // The chosen customer's linked accounts, offered under the AWS account field.
  useEffect(() => {
    setLinked([]);
    if (!api || !open || orgId === "" || errors.orgId) return;
    let cancelled = false;
    api
      .get("/v1/admin/orgs/{orgId}/aws-accounts", { path: { orgId } })
      .then((page) => {
        if (!cancelled) setLinked(linkedAccountChoices(page.items.map(accountRow)));
      })
      .catch((e: unknown) => {
        if (!(e instanceof ApiError)) throw e;
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, orgId]);

  async function submit(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    if (!api || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api.post("/v1/installer/sessions", { body: createSessionRequest(draft) });
      setDraft(emptyCreateSessionDraft);
      onCreated(session.id);
    } catch (e) {
      if (e instanceof ApiError) setError(e);
      else throw e;
    } finally {
      setBusy(false);
    }
  }

  const show = (field: keyof CreateSessionDraft, value: string) => (touched || value.trim() !== "" ? errors[field] : undefined);
  const accountError = show("awsAccountId", draft.awsAccountId);
  const regionsError = show("regions", draft.regions);
  const orgError = touched ? errors.orgId : undefined;
  const installationError = show("installationId", draft.installationId);

  return (
    <Dialog
      title="Start an installer session"
      open={open}
      onClose={onClose}
      class="dialog-wide"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="new-session-form" class="button button-primary" disabled={busy}>
            {busy ? "Starting…" : "Start session"}
          </button>
        </>
      }
    >
      <form id="new-session-form" class="stack" onSubmit={(event) => void submit(event)} noValidate>
        {error && (
          <p role="alert" class="banner banner-error">
            {createSessionErrorMessage(error)}
          </p>
        )}
        <div class="form-grid">
          <Field id="new-session-org" label="Customer" help="common.customer" error={orgError}>
            <CustomerPicker
              id="new-session-org"
              value={draft.orgId}
              onChange={(value) => setDraft({ ...draft, orgId: value })}
              invalid={!!orgError}
              describedBy={orgError ? errorId("new-session-org") : undefined}
            />
          </Field>
          <Field
            id="new-session-account"
            label="AWS account id"
            help="installer.awsAccountId"
            error={accountError}
            hint={
              linked.length > 0
                ? `Linked: ${linked.map((a) => `${a.awsAccountId}${a.verified ? "" : " (not verified)"}`).join(", ")}`
                : "12 digits. The account needs a verified link."
            }
          >
            <input
              id="new-session-account"
              type="text"
              inputMode="numeric"
              class="input-mono"
              autocomplete="off"
              list={linked.length > 0 ? "new-session-linked" : undefined}
              value={draft.awsAccountId}
              aria-invalid={accountError ? "true" : undefined}
              aria-describedby={accountError ? errorId("new-session-account") : hintId("new-session-account")}
              onInput={(e) => setDraft({ ...draft, awsAccountId: e.currentTarget.value })}
            />
            {linked.length > 0 && (
              <datalist id="new-session-linked">
                {linked.map((a) => (
                  <option key={a.awsAccountId} value={a.awsAccountId} />
                ))}
              </datalist>
            )}
          </Field>
          <Field id="new-session-regions" label="Regions" help="installer.regions" error={regionsError} hint="Region codes, separated by commas.">
            <input
              id="new-session-regions"
              type="text"
              placeholder="us-east-1, us-west-2"
              autocomplete="off"
              value={draft.regions}
              aria-invalid={regionsError ? "true" : undefined}
              aria-describedby={regionsError ? errorId("new-session-regions") : hintId("new-session-regions")}
              onInput={(e) => setDraft({ ...draft, regions: e.currentTarget.value })}
            />
          </Field>
          <Field id="new-session-goal" label="Goal" help="installer.goal">
            <select id="new-session-goal" value={draft.goal} onChange={(e) => setDraft({ ...draft, goal: e.currentTarget.value as InstallerGoal })}>
              {GOALS.map((goal) => (
                <option value={goal} key={goal}>
                  {goalLabel(goal)}
                </option>
              ))}
            </select>
          </Field>
          {draft.goal === "upgrade" && (
            <Field id="new-session-installation" label="Installation id" help="installer.installationId" error={installationError}>
              <input
                id="new-session-installation"
                type="text"
                class="input-mono"
                autocomplete="off"
                value={draft.installationId}
                aria-invalid={installationError ? "true" : undefined}
                aria-describedby={installationError ? errorId("new-session-installation") : undefined}
                onInput={(e) => setDraft({ ...draft, installationId: e.currentTarget.value })}
              />
            </Field>
          )}
          <Field id="new-session-access" label="Access mode" help="installer.accessMode" hint={accessModeHint(draft.accessMode)}>
            <select
              id="new-session-access"
              value={draft.accessMode}
              aria-describedby={hintId("new-session-access")}
              onChange={(e) => setDraft({ ...draft, accessMode: e.currentTarget.value as InstallerAccessMode })}
            >
              {ACCESS_MODES.map((mode) => (
                <option value={mode} key={mode}>
                  {accessModeLabel(mode)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
